import React from 'react';
import { FaTimes, FaChartLine, FaInfoCircle } from 'react-icons/fa';

const MetricDetails = ({ selectedMetric, onClose }) => {
  if (!selectedMetric) return null;

  const getMetricDescription = (key) => {
    const descriptions = {
      // Performance Metrics
      execution_duration: 'Total time taken to complete the run execution',
      latency_ms: 'Time taken for the LLM to respond to the request',
      tokens_per_second: 'Rate at which tokens are processed',
      response_time: 'Total time from request to response completion',
      
      // Quality Metrics
      hallucination_score: 'Likelihood of the model generating false information',
      grounding_score: 'How well the response is grounded in the provided context',
      model_confidence: 'Model\'s confidence level in its response',
      complexity_score: 'Complexity level of the generated response',
      
      // Safety Metrics
      toxicity_score: 'Level of toxic content detected in the response',
      bias_score: 'Presence of biased language or content',
      sensitivity_score: 'Sensitivity level of the content',
      hate_speech_detected: 'Whether hate speech was detected in the response',
      violence_detected: 'Whether violent content was detected in the response',
      
      // Cost Metrics
      estimated_cost: 'Estimated cost of the API call',
      total_tokens: 'Total number of tokens used in the request and response',
      input_tokens: 'Number of tokens in the input prompt',
      output_tokens: 'Number of tokens in the generated response',
      
      // RAG Metrics
      answer_faithfulness: 'How faithful the answer is to the source material',
      answer_relevance: 'Relevance of the answer to the query',
      hit_rate: 'Success rate of retrieving relevant information',
      format_correctness: 'Correctness of the response format',
      queries_per_second: 'Rate of queries processed per second',
      error_rate: 'Rate of errors encountered during processing',
      
      // Embedding Metrics
      retrieval_duration_ms: 'Time taken to retrieve relevant documents',
      relevance_score: 'Relevance score of retrieved documents',
      embedding_generation_time_ms: 'Time taken to generate embeddings',
      confidence_score: 'Confidence level in the embedding quality',
      
      // Truthfulness Metrics
      high_faithfulness_score: 'Score for high faithfulness responses (>80%)',
      moderate_faithfulness_score: 'Score for moderate faithfulness responses (60-80%)',
      average_faithfulness: 'Average faithfulness score across all responses',
      
      // Compliance Metrics
      pii_detected: 'Whether Personally Identifiable Information was detected',
      pii_count: 'Number of PII instances found',
      compliance_status: 'Overall compliance status of the response',
      risk_level: 'Risk level assessment of the content',
      alerts_triggered: 'Number of alerts triggered during processing'
    };
    
    return descriptions[key] || 'No description available for this metric.';
  };

  const getMetricSeverity = (value, key) => {
    if (value === null || value === undefined) {
      return { level: 'unknown', color: 'text-gray-500', label: 'Unknown' };
    }

    if (typeof value === 'boolean') {
      return {
        level: value ? 'good' : 'critical',
        color: value ? 'text-green-600' : 'text-red-600',
        label: value ? 'Good' : 'Critical'
      };
    }

    if (typeof value === 'number') {
      const percentage = value * 100;
      
      // For positive metrics (higher is better)
      if (key.includes('score') || key.includes('confidence') || key.includes('faithfulness') || key.includes('relevance')) {
        if (percentage >= 80) return { level: 'excellent', color: 'text-green-600', label: 'Excellent' };
        if (percentage >= 60) return { level: 'good', color: 'text-blue-600', label: 'Good' };
        if (percentage >= 40) return { level: 'fair', color: 'text-yellow-600', label: 'Fair' };
        return { level: 'poor', color: 'text-red-600', label: 'Poor' };
      }
      
      // For negative metrics (lower is better)
      if (key.includes('error') || key.includes('toxicity') || key.includes('bias') || key.includes('hallucination')) {
        if (percentage <= 10) return { level: 'excellent', color: 'text-green-600', label: 'Excellent' };
        if (percentage <= 30) return { level: 'good', color: 'text-blue-600', label: 'Good' };
        if (percentage <= 50) return { level: 'fair', color: 'text-yellow-600', label: 'Fair' };
        return { level: 'poor', color: 'text-red-600', label: 'Poor' };
      }
      
      // For cost metrics
      if (key.includes('cost')) {
        return { level: 'info', color: 'text-blue-600', label: 'Info' };
      }
      
      // For time metrics
      if (key.includes('duration') || key.includes('time') || key.includes('latency')) {
        if (percentage <= 1000) return { level: 'excellent', color: 'text-green-600', label: 'Fast' };
        if (percentage <= 3000) return { level: 'good', color: 'text-blue-600', label: 'Good' };
        if (percentage <= 5000) return { level: 'fair', color: 'text-yellow-600', label: 'Slow' };
        return { level: 'poor', color: 'text-red-600', label: 'Very Slow' };
      }
    }

    return { level: 'info', color: 'text-gray-600', label: 'Info' };
  };

  const severity = getMetricSeverity(selectedMetric.value, selectedMetric.key);

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 mb-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <FaChartLine className="text-blue-600 mr-2" />
          <h3 className="text-lg font-semibold text-gray-800">{selectedMetric.label}</h3>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition-colors"
        >
          <FaTimes />
        </button>
      </div>

      <div className="space-y-4">
        {/* Metric Value */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-500 mb-1">Current Value</div>
              <div className={`text-2xl font-bold ${severity.color}`}>
                {selectedMetric.formattedValue}
              </div>
            </div>
            <div className="text-right">
              <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${severity.color.replace('text-', 'bg-')} bg-opacity-10`}>
                {severity.label}
              </div>
            </div>
          </div>
        </div>

        {/* Metric Details */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-sm text-gray-500 mb-1">Category</div>
            <div className="text-sm font-medium text-gray-800">{selectedMetric.category}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500 mb-1">Metric Key</div>
            <div className="text-sm font-mono text-gray-600 bg-gray-100 px-2 py-1 rounded">
              {selectedMetric.key}
            </div>
          </div>
        </div>

        {/* Description */}
        <div>
          <div className="flex items-center text-sm text-gray-500 mb-2">
            <FaInfoCircle className="mr-1" />
            Description
          </div>
          <div className="text-sm text-gray-700 bg-blue-50 p-3 rounded-lg">
            {getMetricDescription(selectedMetric.key)}
          </div>
        </div>

        {/* Severity Information */}
        <div>
          <div className="text-sm text-gray-500 mb-2">Severity Level</div>
          <div className="flex items-center">
            <div className={`w-3 h-3 rounded-full mr-2 ${severity.color.replace('text-', 'bg-')}`}></div>
            <span className={`text-sm font-medium ${severity.color}`}>
              {severity.label} ({severity.level})
            </span>
          </div>
        </div>

        {/* Recommendations */}
        <div>
          <div className="text-sm text-gray-500 mb-2">Recommendations</div>
          <div className="text-sm text-gray-700">
            {severity.level === 'poor' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="font-medium text-red-800 mb-1">⚠️ Immediate Action Required</div>
                <div className="text-red-700">
                  This metric indicates a critical issue that needs immediate attention. 
                  Consider reviewing your model configuration or input data.
                </div>
              </div>
            )}
            {severity.level === 'fair' && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <div className="font-medium text-yellow-800 mb-1">⚠️ Improvement Needed</div>
                <div className="text-yellow-700">
                  This metric could be improved. Consider optimizing your prompts or model parameters.
                </div>
              </div>
            )}
            {severity.level === 'good' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="font-medium text-blue-800 mb-1">✅ Good Performance</div>
                <div className="text-blue-700">
                  This metric is performing well. Continue monitoring for consistency.
                </div>
              </div>
            )}
            {severity.level === 'excellent' && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="font-medium text-green-800 mb-1">🎉 Excellent Performance</div>
                <div className="text-green-700">
                  This metric is performing exceptionally well. Maintain current configuration.
                </div>
              </div>
            )}
            {severity.level === 'info' && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <div className="font-medium text-gray-800 mb-1">ℹ️ Informational</div>
                <div className="text-gray-700">
                  This is an informational metric for monitoring purposes.
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MetricDetails; 